import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useMemo,
} from "react";
import {
  useReservations,
  useReservation,
  useCreateReservation,
  useUpdateReservation,
} from "@/hooks/useReservations";
import { GuestStatus, ReservationFilters } from "@/types/reservation";
import { DateRange } from "@/hooks/useDateRange";
import { GUEST_STATUS_FILTER_DEFAULT } from "@/components/pages/reservations/utils/reservationGuestStatusFilters";
import {
  mapReservationStatusFilterToStatuses,
  RESERVATION_STATUS_FILTER_DEFAULT,
} from "@/components/pages/reservations/utils/reservationStatusFilters";

// Sorting types
type SortField =
  | "status"
  | "guest"
  | "bookingNumber"
  | "roomNumber"
  | "arrivalDate"
  | "departureDate";
type SortDirection = "asc" | "desc" | null;

const today = new Date();
today.setHours(0, 0, 0, 0);

const futureDate = new Date();
futureDate.setDate(today.getDate() + 100);
futureDate.setHours(0, 0, 0, 0);

interface ReservationContextType {
  // Query hooks
  useReservations: (
    page?: number,
    limit?: number,
    filters?: ReservationFilters,
  ) => ReturnType<typeof useReservations>;
  useReservation: (id: string) => ReturnType<typeof useReservation>;

  // Mutation hooks
  useCreateReservation: () => ReturnType<typeof useCreateReservation>;
  useUpdateReservation: (id: string) => ReturnType<typeof useUpdateReservation>;

  // Filter and pagination state
  filters: {
    statusFilter: string;
    guestStatusFilter: string;
    countryFilter: string;
    balanceFilter: string;
    dateRange: DateRange;
    searchTerm: string;
    currentPage: number;
    sortField: SortField | null;
    sortDirection: SortDirection;
    selectedReservations: string[];
  };

  // Filter and pagination actions
  actions: {
    setStatusFilter: (value: string) => void;
    setGuestStatusFilter: (value: string) => void;
    setCountryFilter: (value: string) => void;
    setBalanceFilter: (value: string) => void;
    setDateRange: (dateRange: DateRange) => void;
    setSearchTerm: (value: string) => void;
    setCurrentPage: (page: number) => void;
    setSortField: (field: SortField | null) => void;
    setSortDirection: (direction: SortDirection) => void;
    setSelectedReservations: (ids: string[]) => void;
    handleSort: (field: string) => void;
    handleFilterChange: (
      filterSetter: (value: string) => void,
      value: string,
    ) => void;
    handleDateRangeChange: (dateRange: DateRange) => void;
    handleSelectReservation: (id: string, checked: boolean) => void;
    handleSelectAll: (checked: boolean, reservationIds: string[]) => void;
    resetFilters: () => void;
  };

  // Computed values
  computed: {
    apiFilters: ReservationFilters;
    limit: number;
  };
}

const ReservationContext = createContext<ReservationContextType | undefined>(
  undefined,
);

interface ReservationProviderProps {
  children: ReactNode;
}

const defaultDateRange: DateRange = {
  from: today,
  to: futureDate,
};
export const ReservationProvider: React.FC<ReservationProviderProps> = ({
  children,
}) => {
  // Filter and pagination state
  const [statusFilter, setStatusFilter] = useState<string>(
    RESERVATION_STATUS_FILTER_DEFAULT,
  );
  const [guestStatusFilter, setGuestStatusFilter] = useState<string>(
    GUEST_STATUS_FILTER_DEFAULT,
  );
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [balanceFilter, setBalanceFilter] = useState<string>("all balances");
  const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [selectedReservations, setSelectedReservations] = useState<string[]>(
    [],
  );
  const limit = 10;

  // Handle sorting
  const handleSort = (field: string) => {
    const newSortField = field as SortField;

    if (sortField === newSortField) {
      let nextDirection: SortDirection;

      if (sortDirection === "asc") {
        nextDirection = "desc";
      } else if (sortDirection === "desc") {
        nextDirection = null;
      } else {
        nextDirection = "asc";
      }

      setSortDirection(nextDirection);

      if (nextDirection === null) {
        setSortField(null);
      }
    } else {
      setSortField(newSortField);
      setSortDirection("asc");
    }

    setCurrentPage(1);
  };

  // Reset to page 1 when filters change
  const handleFilterChange = (
    filterSetter: (value: string) => void,
    value: string,
  ) => {
    filterSetter(value);
    setCurrentPage(1);
  };

  // Handle date range changes
  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
    setCurrentPage(1);
  };

  // Handle reservation selection
  const handleSelectReservation = (id: string, checked: boolean) => {
    setSelectedReservations((prev) =>
      checked ? [...prev, id] : prev.filter((resId) => resId !== id),
    );
  };

  const handleSelectAll = (checked: boolean, reservationIds: string[]) => {
    if (checked) {
      setSelectedReservations(reservationIds);
    } else {
      setSelectedReservations([]);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setStatusFilter(RESERVATION_STATUS_FILTER_DEFAULT);
    setGuestStatusFilter(GUEST_STATUS_FILTER_DEFAULT);
    setCountryFilter("all");
    setDateRange(defaultDateRange);
    setSearchTerm("");
    setCurrentPage(1);
    setSortField(null);
    setSortDirection(null);
    setSelectedReservations([]);
  };

  // Build API filters
  const apiFilters: ReservationFilters = useMemo(() => {
    const filters: ReservationFilters = {};

    const selectedStatuses = mapReservationStatusFilterToStatuses(statusFilter);
    if (selectedStatuses.length > 0) {
      filters.status = selectedStatuses;
    }
    if (guestStatusFilter !== GUEST_STATUS_FILTER_DEFAULT) {
      filters.guestStatus = [guestStatusFilter as GuestStatus];
    }
    if (countryFilter !== "all") {
      filters.country = [countryFilter];
    }
    if (dateRange) {
      filters.dateRange = dateRange;
    }
    if (searchTerm) {
      filters.search = searchTerm;
    }
    if (sortField && sortDirection) {
      filters.sort = `${sortField}:${sortDirection}`;
    }

    filters.page = currentPage;

    return filters;
  }, [
    statusFilter,
    guestStatusFilter,
    countryFilter,
    dateRange,
    searchTerm,
    sortField,
    sortDirection,
    currentPage,
  ]);

  const contextValue: ReservationContextType = {
    useReservations,
    useReservation,
    useCreateReservation,
    useUpdateReservation,
    filters: {
      statusFilter,
      guestStatusFilter,
      countryFilter,
      balanceFilter,
      dateRange,
      searchTerm,
      currentPage,
      sortField,
      sortDirection,
      selectedReservations,
    },
    actions: {
      setStatusFilter,
      setGuestStatusFilter,
      setCountryFilter,
      setBalanceFilter,
      setDateRange,
      setSearchTerm,
      setCurrentPage,
      setSortField,
      setSortDirection,
      setSelectedReservations,
      handleSort,
      handleFilterChange,
      handleDateRangeChange,
      handleSelectReservation,
      handleSelectAll,
      resetFilters,
    },
    computed: {
      apiFilters,
      limit,
    },
  };

  return (
    <ReservationContext.Provider value={contextValue}>
      {children}
    </ReservationContext.Provider>
  );
};

export const useReservationContext = () => {
  const context = useContext(ReservationContext);
  if (context === undefined) {
    throw new Error(
      "useReservationContext must be used within a ReservationProvider",
    );
  }
  return context;
};
